import React from 'react';
import styled from '@emotion/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Icon } from '@uhg-abyss/web/ui/Icon';
import { AIInsight, CommonIssue, ValidationResults } from '../types';
import { Theme } from '../styles/theme';

// Preserved for the richer insight-card layout; ignored until it is wired into JSX.
/* istanbul ignore next */
const InsightsContainer = styled.div<{ bgGradient: string }>`
  background: ${props => props.bgGradient};
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;
`;

/* istanbul ignore next */
const Title = styled.h3`
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

/* istanbul ignore next */
const HealthScoreSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
`;

/* istanbul ignore next */
const ScoreCircle = styled.div<{ scoreColor: string }>`
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.scoreColor};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

/* istanbul ignore next */
const ScoreLabel = styled.div`
  color: white;
`;

/* istanbul ignore next */
const ScoreTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
`;

/* istanbul ignore next */
const ScoreDescription = styled.div`
  font-size: 14px;
  opacity: 0.9;
`;

/* istanbul ignore next */
const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

/* istanbul ignore next */
const InsightCard = styled.div<{ priority: string; colors: Theme['colors'] }>`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 16px;
  border-left: 4px solid ${props =>
    props.priority === 'high' ? props.colors.error :
    props.priority === 'medium' ? props.colors.warning :
    props.colors.success
  };
`;

/* istanbul ignore next */
const InsightTitle = styled.div<{ color: string }>`
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 8px;
  font-size: 15px;
`;

/* istanbul ignore next */
const InsightMessage = styled.div<{ color: string }>`
  color: ${props => props.color};
  font-size: 14px;
  line-height: 1.5;
`;

/* istanbul ignore next */
const CommonIssuesSection = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 20px;
`;

/* istanbul ignore next */
const IssuesTitle = styled.h4<{ color: string }>`
  color: ${props => props.color};
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
`;

/* istanbul ignore next */
const IssueItem = styled.div<{ borderColor: string }>`
  padding: 12px;
  border-left: 3px solid ${props => props.borderColor};
  background: #f8f9fa;
  border-radius: 0 8px 8px 0;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

/* istanbul ignore next */
const IssueField = styled.div<{ color: string }>`
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 4px;
`;

/* istanbul ignore next */
const IssueCount = styled.span<{ bgColor: string; color: string }>`
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.bgColor};
  color: ${props => props.color};
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
`;

/* istanbul ignore next */
const IssueMessage = styled.div<{ color: string }>`
  font-size: 14px;
  color: ${props => props.color};
  margin-bottom: 8px;
`;

/* istanbul ignore next */
const IssueFix = styled.div<{ color: string }>`
  font-size: 13px;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface AIInsightsProps {
  results: ValidationResults;
  insights: AIInsight[];
  commonIssues: CommonIssue[];
  healthScore: number;
}

export const AIInsights: React.FC<AIInsightsProps> = ({ results, insights, commonIssues, healthScore }) => {
  if (!insights || insights.length === 0) {
    return (
      <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>

        {commonIssues && commonIssues.length > 0 && (
          <Card>
            <Text fontWeight="bold" fontSize="lg">Top Issues to Fix:</Text>
            {commonIssues.map((issue, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <Text fontWeight="medium">{issue.issue} <Badge>{issue.count}</Badge></Text>
                <Text color="#666">{issue.suggestion}</Text>
                <Text color="#666">Affects lines: {issue.affectedLines.slice(0,5).join(', ')}{issue.affectedLines.length > 5 ? '...' : ''}</Text>
              </div>
            ))}
          </Card>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>

      {insights.map((ins, idx) => (
        <Card key={idx}>
          <Text fontWeight="bold" fontSize="lg">{ins.title}: {ins.description}</Text>
        </Card>
      ))}

      {commonIssues && commonIssues.length > 0 && (
        <Card>
          <Text fontWeight="bold" fontSize="lg">Top Issues to Fix:</Text>
          {commonIssues.map((issue, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <Text fontWeight="medium">{issue.issue} <Badge>{issue.count}</Badge></Text>
              <Text color="#666">{issue.suggestion}</Text>
              <Text color="#666">Affects lines: {issue.affectedLines.slice(0,5).join(', ')}{issue.affectedLines.length > 5 ? '...' : ''}</Text>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};
